import {
  CustomMouseEvent,
  defaultStrokeColor,
  mapActive,
} from "../utils/map-utils";
import { CustomSource } from "../models/CustomSource";
import { RootStore } from "../../shared/stores/RootStore";
import { HlaWay } from "../../ra/models/HlaWay";

export class HlaMapSource {
  map: mapboxgl.Map;
  rootStore: RootStore;
  lineClickListenerRemovers: (() => void)[] = [];

  strokeColor = defaultStrokeColor;

  constructor(map: mapboxgl.Map, rootStore: RootStore) {
    this.map = map;
    this.rootStore = rootStore;
    this.config();
  }

  clear() {
    this.lineClickListenerRemovers.map((fn) => fn());
    this.lineClickListenerRemovers = [];

    if (mapActive(this.map)) {
      this.map.removeLayer(strokeLayerConfig.id);
      this.map.removeLayer(lineLayerConfig.id);
      this.map.removeLayer(directionLayerConfig.id);
      this.map.removeLayer(lineMultiSelectedLayerConfig.id);
      this.map.removeSource(sourceName);
    }
  }

  private config() {
    if (mapActive(this.map) && !this.map.getSource(sourceName)) {
      this.map.addSource(sourceName, sourceConfig);
      this.map.addLayer(strokeLayerConfig);
      this.map.addLayer(lineLayerConfig);
      this.map.addLayer(directionLayerConfig);
      this.map.addLayer(lineMultiSelectedLayerConfig);
    }
  }

  updateData() {
    const { hlaStore, filterStore } = this.rootStore;

    if (mapActive(this.map)) {
      const selectedWay = hlaStore.getSelected();
      const source = this.map.getSource(sourceName) as mapboxgl.GeoJSONSource;
      source?.setData({
        type: "FeatureCollection",
        features: filterStore.showHla
          ? hlaStore.ways.map((way) => {
              var color = filterStore.isHlaWayVisible(way)
                ? this.getLineColor(way)
                : defaultStrokeColor;
              var strokeWidth = 7;
              if (selectedWay?.id === way.id) {
                strokeWidth = 12;
              }
              const properties: HlaLineProps = {
                color: color,
                strokeColor: defaultStrokeColor,
                strokeWidth: strokeWidth,
                hlaWayId: way.id,
                isMultiSelected: hlaStore.multiSelectedIds.has(way.id),
              };
              return {
                type: "Feature",
                properties,
                geometry: {
                  type: "LineString",
                  coordinates: way.coordinates,
                },
              };
            })
          : [],
      });
    }
  }

  getLineColor(way: HlaWay): string {
    const { filterStore } = this.rootStore;
    const { frontFacingLane: frontLane, backFacingLane: backLane } = way;

    const frontScore =
      frontLane && filterStore.isHlaLaneVisible(frontLane)
        ? frontLane.score?.global
        : undefined;
    const backScore =
      backLane && filterStore.isHlaLaneVisible(backLane)
        ? backLane.score?.global
        : undefined;

    if (frontScore) {
      if (backScore) {
        return frontScore.value > backScore.value
          ? frontScore.color
          : backScore.color;
      }
      return frontScore.color;
    }
    if (backScore) return backScore.color;
    return "#FFFFFF";
  }

  enableLineClick() {
    const { hlaStore } = this.rootStore;
    const canvas = this.map.getCanvasContainer();

    const onMouseDown = (e: CustomMouseEvent) => {
      e.preventDefault();
      canvas.style.cursor = "grab";
      const feature = e.features?.[0]!;
      const props = feature.properties as HlaLineProps;

      this.map.once("mouseup", () => {
        canvas.style.cursor = "";
        hlaStore.select(props.hlaWayId);
      });
    };

    const cursorPointer = () => (canvas.style.cursor = "pointer");
    const cursorDefault = () => (canvas.style.cursor = "");

    this.map.on("mouseenter", lineLayerName, cursorPointer);
    this.map.on("mouseleave", lineLayerName, cursorDefault);
    this.map.on("mousedown", lineLayerName, onMouseDown);

    this.lineClickListenerRemovers.push(() => {
      this.map.off("mouseenter", lineLayerName, cursorPointer);
      this.map.off("mouseleave", lineLayerName, cursorDefault);
      this.map.off("mousedown", lineLayerName, onMouseDown);
    });
  }

  disableLineClick() {
    this.lineClickListenerRemovers.map((fn) => fn());
    this.lineClickListenerRemovers = [];
  }

  extendBounds(bounds: mapboxgl.LngLatBounds) {
    const { osaNodeStore, osaWayStore, hlaStore, filterStore } = this.rootStore;

    const selectedOsaNode = osaNodeStore.getSelected();
    const selectedOsaWay = osaWayStore.getSelected();
    const selectedHlaWay = hlaStore.getSelected();
    const hlaWays = filterStore.filteredHlaWays;

    if (selectedOsaNode || selectedOsaWay) return;
    hlaWays.forEach((hlaWay) => {
      if (selectedHlaWay && selectedHlaWay.id !== hlaWay.id) return;

      hlaWay.coordinates.forEach((coords) => {
        bounds.extend(coords as [number, number]);
      });
    });
  }
}

const isLineString = ["==", "$type", "LineString"];
const isMultiNodeSelected = ["boolean", ["get", "isMultiSelected"], false];

const sourceName = CustomSource.Hla;
const sourceConfig: mapboxgl.GeoJSONSourceSpecification = {
  type: "geojson",
  promoteId: "id",
  data: {
    type: "FeatureCollection",
    features: [],
  },
};

const lineLayerName = `${sourceName}-line`;
const lineLayerConfig: mapboxgl.LineLayerSpecification = {
  id: lineLayerName,
  source: sourceName,
  type: "line",
  layout: {
    "line-join": "round",
    "line-cap": "round",
  },
  paint: {
    "line-color": ["get", "color"],
    "line-width": 5,
  },
  filter: isLineString,
};

const strokeLayerName = `${sourceName}-stroke`;
const strokeLayerConfig: mapboxgl.LineLayerSpecification = {
  id: strokeLayerName,
  source: sourceName,
  type: "line",
  layout: {
    "line-join": "round",
    "line-cap": "round",
  },
  paint: {
    "line-color": ["get", "strokeColor"],
    "line-width": ["get", "strokeWidth"],
  },
  filter: isLineString,
};

// Adds arrows to the way lines so that you know the direction
const directionLayerName = `${sourceName}-direction`;
const directionLayerConfig: mapboxgl.SymbolLayerSpecification = {
  id: directionLayerName,
  source: sourceName,
  type: "symbol",
  layout: {
    "symbol-placement": "line",
    "symbol-spacing": 50,
    "icon-image": "chevron-icon",
    "icon-rotation-alignment": "map",
    "icon-size": 0.5,
  },
  paint: {
    "icon-color": defaultStrokeColor,
  },
};

// The blue line that appears when you select multiple ways
const lineMultiSelectedLayerName = `${sourceName}-multi-selected-line`;
const lineMultiSelectedLayerConfig: mapboxgl.LineLayerSpecification = {
  type: "line",
  layout: {
    "line-join": "round",
    "line-cap": "round",
  },
  id: lineMultiSelectedLayerName,
  source: sourceName,
  paint: {
    "line-width": ["case", isMultiNodeSelected, 3, 0],
    "line-color": "#00b7db",
  },
  filter: isLineString,
};

export interface HlaLineProps {
  hlaWayId: string;
  color: string;
  strokeColor: string;
  strokeWidth: number;
  isMultiSelected: boolean;
  "score.global"?: string;
  "score.bicycleToBicycle"?: string;
  "score.bicycleToPedestrian"?: string;
  "score.singleBicycle"?: string;
  "score.vehicleToBicycle"?: string;
}

export const hlaWayShiftDragLayerName = lineLayerName;
